import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { IconArrowsMaximize, IconExternalLink } from "@tabler/icons-react";

import { MandorMobile } from "./MandorMobile";

/**
 * Mobile view · presentable inside the office web.
 * Puts the real /mandor page inside a phone-frame chrome so the
 * whole field UX is demoable from a laptop projector — no LAN link,
 * no phone required. On actual phone-sized screens the frame is
 * dropped and MandorMobile takes the whole viewport.
 */
export function MobilePreview() {
  const [isNarrow, setIsNarrow] = useState(() =>
    typeof window !== "undefined" &&
    window.matchMedia("(max-width: 767px)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const on = (e: MediaQueryListEvent) => setIsNarrow(e.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  // On phones · skip the frame, render the mobile page raw.
  if (isNarrow) return <MandorMobile />;

  return (
    <div className="space-y-4 text-sm">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="text-lg font-medium">Mobile view · preview</div>
          <p className="text-xs text-text-secondary">
            The exact page the Mandor / Driver / Dispatch sees on the phone
            (Ulefone rugged). Use the persona switcher inside the frame to
            walk through every field role from this laptop.
          </p>
        </div>
        <Link
          to="/mandor"
          className="flex items-center gap-1 rounded border border-border bg-surface-2 px-3 py-1.5 text-xs hover:bg-surface-1"
          title="Open full-screen (like on a phone)"
        >
          <IconArrowsMaximize size={13} />
          Full screen
        </Link>
      </div>

      <div className="flex flex-col items-center gap-3 rounded-xl border border-border bg-surface-1 p-4 md:p-6">
        {/* Phone-frame chrome */}
        <div className="relative w-full max-w-[420px] rounded-[36px] border border-slate-700 bg-slate-900 p-3 shadow-lg">
          {/* Notch */}
          <div className="absolute left-1/2 top-3 z-10 h-5 w-24 -translate-x-1/2 rounded-b-2xl bg-slate-900" />
          <div className="relative h-[720px] w-full overflow-hidden rounded-[28px] bg-white">
            {/* Fake status bar */}
            <div className="flex items-center justify-between bg-white px-5 pt-3 pb-1 text-[11px] font-medium text-slate-800">
              <span>
                {new Date().toLocaleTimeString("en-MY", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
              <div className="flex items-center gap-1.5 text-[10px]">
                <span>●●●●</span>
                <span>Wi-Fi</span>
                <span>92%</span>
              </div>
            </div>
            {/* The real mobile page · scrollable inside the frame */}
            <div className="h-[calc(100%-24px)] overflow-y-auto">
              <MandorMobile />
            </div>
          </div>
          {/* Home indicator */}
          <div className="mt-2 flex justify-center">
            <div className="h-1 w-24 rounded-full bg-slate-700" />
          </div>
        </div>

        <div className="max-w-[420px] text-center text-[11px] text-text-secondary">
          Same page. Same endpoints. Same data. This is what the phone shows,
          rendered inside a device chrome so you can demo without the phone.
          <br />
          <a
            href="/mandor"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 underline"
          >
            Open in a new tab <IconExternalLink size={11} />
          </a>{" "}
          to project it full-screen.
        </div>
      </div>
    </div>
  );
}
